import React, { useCallback, useState } from 'react';
import { api, buildEmailOpenPixelUrl } from '../../lib/api';
import { useToast } from '../ui/Toast';
import { Button } from '../ui/Kit';
import {
  appendTrackingPixel,
  buildGmailComposeUrl,
  copyHtmlViaSelection,
  writeEmailHtmlToClipboard,
} from '../../lib/emailCompose';

const TRACKING_NOTE = "Opens are tracked approximately; clicks aren't tracked.";

function isAlreadyLogged(err) {
  return err?.code === '23505' || /already logged/i.test(err?.message || '');
}

/**
 * "Send with Gmail": copy sanitized HTML (+ open pixel) → open Gmail compose →
 * log as gmail_manual with the same tracking token.
 * `start` must be called synchronously from the click handler (clipboard needs
 * the user activation), and Gmail is only opened once the copy has succeeded.
 * The logged body_html never contains the pixel, so viewing history can't
 * register an open.
 */
export default function useGmailManualSend({ leadId, toEmail, onLogged }) {
  const { addToast } = useToast();
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  const logSend = useCallback(
    async ({ html, subject, token }) => {
      try {
        await api.emailLogs.logGmailManualEmail({
          leadId,
          subject,
          bodyHtml: html,
          toEmail,
          trackingToken: token,
        });
        await onLogged?.();
      } catch (err) {
        if (isAlreadyLogged(err)) {
          addToast({ type: 'info', title: err.message, duration: 6000 });
          await onLogged?.();
          return;
        }
        addToast({
          type: 'error',
          title: "Couldn't log the Gmail email",
          message: err?.message || 'Unknown error',
          duration: 8000,
        });
      } finally {
        setBusy(false);
      }
    },
    [leadId, toEmail, onLogged, addToast],
  );

  const openGmailAndLog = useCallback(
    ({ html, clipHtml, subject, token, format }) => {
      const url = buildGmailComposeUrl({ to: toEmail, subject });
      const win = window.open(url, '_blank');
      if (!win) {
        setNotice({ kind: 'popup_blocked', html, clipHtml, subject, token, url });
        setBusy(false);
        return;
      }
      win.opener = null;
      setNotice(null);
      addToast({
        type: 'success',
        title: 'Email copied — Gmail is open',
        message:
          (format === 'text'
            ? 'Copied as plain text (formatting not supported by this browser). '
            : 'Paste with Ctrl/Cmd+V in the Gmail message body. ') + TRACKING_NOTE,
        duration: 8000,
      });
      logSend({ html, subject, token });
    },
    [toEmail, addToast, logSend],
  );

  const start = useCallback(
    ({ html, subject }) => {
      const token = crypto.randomUUID();
      const clipHtml = appendTrackingPixel(html, buildEmailOpenPixelUrl(token));
      let copy;
      try {
        copy = writeEmailHtmlToClipboard(clipHtml);
      } catch (err) {
        copy = Promise.reject(err);
      }
      setBusy(true);
      setNotice(null);
      copy.then(
        (format) => openGmailAndLog({ html, clipHtml, subject, token, format }),
        (err) => {
          setBusy(false);
          setNotice({ kind: 'copy_failed', html, clipHtml, subject, token, error: err?.message });
          addToast({
            type: 'error',
            title: "Couldn't copy the email",
            message: 'Gmail was not opened. Use "Copy HTML" to copy it, then Gmail will open.',
            duration: 8000,
          });
        },
      );
    },
    [openGmailAndLog, addToast],
  );

  const copyHtml = useCallback(() => {
    if (!notice?.clipHtml) return;
    const { html, clipHtml, subject, token } = notice;
    if (copyHtmlViaSelection(clipHtml)) {
      setBusy(true);
      openGmailAndLog({ html, clipHtml, subject, token, format: 'html' });
      return;
    }
    let copy;
    try {
      copy = writeEmailHtmlToClipboard(clipHtml);
    } catch (err) {
      copy = Promise.reject(err);
    }
    copy.then(
      (format) => {
        setBusy(true);
        openGmailAndLog({ html, clipHtml, subject, token, format });
      },
      () => setNotice((n) => ({ ...n, error: 'Your browser blocked clipboard access.' })),
    );
  }, [notice, openGmailAndLog]);

  const openBlockedGmail = useCallback(() => {
    if (!notice?.html) return;
    const { html, subject, token } = notice;
    setNotice(null);
    setBusy(true);
    addToast({
      type: 'success',
      title: 'Gmail is open',
      message: `Paste with Ctrl/Cmd+V. ${TRACKING_NOTE}`,
      duration: 8000,
    });
    logSend({ html, subject, token });
  }, [notice, addToast, logSend]);

  const dismiss = useCallback(() => setNotice(null), []);

  return { start, busy, notice, copyHtml, openBlockedGmail, dismiss };
}

export function GmailSendNotice({ gmail, className = '' }) {
  const { notice, copyHtml, openBlockedGmail, dismiss } = gmail;
  if (!notice) return null;

  if (notice.kind === 'popup_blocked') {
    return (
      <div className={`rounded-md border border-warning/40 bg-warningBg px-3 py-2 text-xs text-gray-800 ${className}`}>
        <p className="font-semibold text-warning mb-1">Email copied, but the Gmail tab was blocked</p>
        <p className="mb-2">Your browser blocked the pop-up. Open Gmail manually and paste with Ctrl/Cmd+V.</p>
        <div className="flex gap-2">
          <a
            href={notice.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={openBlockedGmail}
            className="inline-flex h-7 items-center rounded-[10px] bg-primary px-3 text-xs font-medium text-white hover:bg-primary-dark"
          >
            Open Gmail
          </a>
          <Button size="sm" variant="ghost" className="h-7" onClick={dismiss}>Dismiss</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-gray-800 ${className}`}>
      <p className="font-semibold text-red-700 mb-1">Couldn&apos;t copy the email to your clipboard</p>
      <p className="mb-2">
        Gmail was not opened. Click &quot;Copy HTML&quot; to copy it — Gmail opens once the copy succeeds.
        {notice.error ? <span className="block text-gray-500 mt-0.5">{notice.error}</span> : null}
      </p>
      <div className="flex gap-2">
        <Button size="sm" variant="primary" className="h-7" onClick={copyHtml}>Copy HTML</Button>
        <Button size="sm" variant="ghost" className="h-7" onClick={dismiss}>Dismiss</Button>
      </div>
    </div>
  );
}
